import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { CreditScoreConfig, Rule } from '../types';
import axios from 'axios';
import { logAction } from './auditLogSlice';
import { RootState } from '.';

const backendUrl = 'http://localhost:3001/api';

export const initialState: CreditScoreConfig = {
  baseScore: 600,
  minMonthlyIncome: 2000,
  minLoanAmount: 5000,
  maxLoanAmount: 1000000,
  interestRates: {
    '800+': 5,
    '700-799': 8,
    '600-699': 12,
    '500-599': 18,
  },
  rules: [],
  smeRules: [],
  darkMode: false,
  // Advanced scoring configuration
  fraudDetectionEnabled: true,
  mlScoringEnabled: true,
  alternativeDataEnabled: true,
  riskBasedPricingEnabled: true,
};

export const fetchRules = createAsyncThunk('config/fetchRules', async () => {
  const response = await axios.get(`${backendUrl}/rules`);
  return response.data;
});

export const fetchSmeRules = createAsyncThunk('config/fetchSmeRules', async () => {
    const response = await axios.get(`${backendUrl}/sme-rules`);
    return response.data;
});

const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {
    _updateConfig: (state, action: PayloadAction<Partial<CreditScoreConfig>>) => {
      return { ...state, ...action.payload };
    },
    _addRule: (state, action: PayloadAction<Rule>) => {
      state.rules.push(action.payload);
    },
    _updateRule: (state, action: PayloadAction<Rule>) => {
      const index = state.rules.findIndex(rule => rule.id === action.payload.id);
      if (index !== -1) {
        state.rules[index] = action.payload;
      }
    },
    _deleteRule: (state, action: PayloadAction<string>) => {
      state.rules = state.rules.filter(rule => rule.id !== action.payload);
    },
    _addSmeRule: (state, action: PayloadAction<Rule>) => {
      state.smeRules.push(action.payload);
    },
    _updateSmeRule: (state, action: PayloadAction<Rule>) => {
      const index = state.smeRules.findIndex(rule => rule.id === action.payload.id);
      if (index !== -1) {
        state.smeRules[index] = action.payload;
      }
    },
    _deleteSmeRule: (state, action: PayloadAction<string>) => {
      state.smeRules = state.smeRules.filter(rule => rule.id !== action.payload);
    },
    toggleDarkMode: (state) => {
      state.darkMode = !state.darkMode;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRules.fulfilled, (state, action: PayloadAction<Rule[]>) => {
        state.rules = action.payload;
      })
      .addCase(fetchSmeRules.fulfilled, (state, action: PayloadAction<Rule[]>) => {
        state.smeRules = action.payload;
      });
  },
});

const { _addRule, _updateRule, _deleteRule, _addSmeRule, _updateSmeRule, _deleteSmeRule } = configSlice.actions;

export const { toggleDarkMode } = configSlice.actions;

// Thunks for audited actions
export const addRule = createAsyncThunk('config/addRule', async (rule: Rule, { dispatch, getState }) => {
    const { auth: { user } } = getState() as RootState;
    const res = await axios.post(`${backendUrl}/rules`, rule);
    dispatch(_addRule(res.data));
    dispatch(logAction({
        action: 'ADD_RULE',
        user: user?.email || 'System',
        details: { ruleName: rule.name, ruleId: rule.id }
    }));
});

export const updateRule = createAsyncThunk('config/updateRule', async (rule: Rule, { dispatch, getState }) => {
    const { auth: { user } } = getState() as RootState;
    await axios.put(`${backendUrl}/rules/${rule.id}`, rule);
    dispatch(_updateRule(rule));
    dispatch(logAction({
        action: 'UPDATE_RULE',
        user: user?.email || 'System',
        details: { ruleName: rule.name, ruleId: rule.id }
    }));
});

export const deleteRule = createAsyncThunk('config/deleteRule', async (ruleId: string, { dispatch, getState }) => {
    const { auth: { user }, config: { rules } } = getState() as RootState;
    const rule = rules.find(r => r.id === ruleId);
    await axios.delete(`${backendUrl}/rules/${ruleId}`);
    dispatch(_deleteRule(ruleId));
    dispatch(logAction({
        action: 'DELETE_RULE',
        user: user?.email || 'System',
        details: { ruleName: rule?.name, ruleId }
    }));
});

export const addSmeRule = createAsyncThunk('config/addSmeRule', async (rule: Rule, { dispatch, getState }) => {
    const { auth: { user } } = getState() as RootState;
    const res = await axios.post(`${backendUrl}/sme-rules`, rule);
    dispatch(_addSmeRule(res.data));
    dispatch(logAction({
        action: 'ADD_SME_RULE',
        user: user?.email || 'System',
        details: { ruleName: rule.name, ruleId: rule.id }
    }));
});

export const updateSmeRule = createAsyncThunk('config/updateSmeRule', async (rule: Rule, { dispatch, getState }) => {
    const { auth: { user } } = getState() as RootState;
    await axios.put(`${backendUrl}/sme-rules/${rule.id}`, rule);
    dispatch(_updateSmeRule(rule));
    dispatch(logAction({
        action: 'UPDATE_SME_RULE',
        user: user?.email || 'System',
        details: { ruleName: rule.name, ruleId: rule.id }
    }));
});

export const deleteSmeRule = createAsyncThunk('config/deleteSmeRule', async (ruleId: string, { dispatch, getState }) => {
    const { auth: { user }, config: { smeRules } } = getState() as RootState;
    const rule = smeRules.find(r => r.id === ruleId);
    await axios.delete(`${backendUrl}/sme-rules/${ruleId}`);
    dispatch(_deleteSmeRule(ruleId));
    dispatch(logAction({
        action: 'DELETE_SME_RULE',
        user: user?.email || 'System',
        details: { ruleName: rule?.name, ruleId }
    }));
});

export const configInitialState = initialState;

export default configSlice.reducer; 